import React, { useCallback, useMemo, useState } from "react";
import { Box } from "@mui/material";
import { styled } from "@mui/styles";
import { orderBy as _orderBy } from "lodash";
import { gaps } from "theme/defaultStyle";
import ActionButton from "controls/global/action-button/ActionButton";
import ColumnsConfig, {
  MenuColumnItem,
} from "controls/global/columns-config/ColumnsConfig";
import { PremiumReceivablesTransactionRecord } from "entities/UIModel/PremiumReceivablesTransactionRecord";
import { useColumnSettings } from "utils/custom-hooks/useColumnSettings";
import { usePremiumReceivableTypeOptions } from "utils/custom-hooks/usePremiumReceivableTypeOptions";
import {
  exportPremiumReceivable,
  PremiumReceivableCriteriaDto,
} from "utils/api/premiumReceivableApi";
import ExportButton from "../../policy-management/shared/ExportButton";
import { serializeFilters } from "../usePremiumReceivableList";
import {
  buildPrColumns,
  PR_DATE_TYPE_OPTIONS,
  PR_GRID_NAME,
} from "./prColumns";
import TransactionsTable from "./TransactionsTable";
import AddPremiumReceivablesModal from "../modals/AddPremiumReceivablesModal";
import {
  YEAR_MONTH_FORMAT_ERROR,
  YEAR_MONTH_REGEX,
  toAccountingPeriod,
} from "../shared/dialogHelper";
import AdvancedSearchPanel, {
  AdvancedSearchPanelField,
  AdvancedSearchPanelSection,
  parseIsoDate,
} from "pages/utils/AdvancedSearchPanel";
import YearMonthNumberFormat from "pages/utils/YearMonthNumberFormat";
import { OptionRenderType } from "controls/global/stewart-checkbox/StewartAutocompleteWithCheckboxes";
import AdvancedSearchButton from "pages/utils/AdvancedSearchButton";

interface AdvancedSearchCriteria {
  dateType: string;
  dateFrom: string;
  dateTo: string;
  uwRetentionMin: string;
  uwRetentionMax: string;
  policiesMin: string;
  policiesMax: string;
}

type RangeEnd = "min" | "max";

// Panel field keys, grouped so an inverted range can be flagged on whichever
// end the user touched last.
const ADVANCED_RANGES: { group: string; min: string; max: string }[] = [
  { group: "date", min: "dateFrom", max: "dateTo" },
  { group: "period", min: "periodFrom", max: "periodTo" },
  { group: "uwRetention", min: "uwRetentionMin", max: "uwRetentionMax" },
  { group: "policies", min: "policiesMin", max: "policiesMax" },
];

const EMPTY_ADVANCED_VALUES: Record<string, string> = {
  dateType: "",
  dateFrom: "",
  dateTo: "",
  periodFrom: "",
  periodTo: "",
  uwRetentionMin: "",
  uwRetentionMax: "",
  policiesMin: "",
  policiesMax: "",
};

const EMPTY_ADVANCED_CRITERIA: AdvancedSearchCriteria = {
  dateType: "",
  dateFrom: "",
  dateTo: "",
  uwRetentionMin: "",
  uwRetentionMax: "",
  policiesMin: "",
  policiesMax: "",
};

const OUT_OF_RANGE_TEXT = "Date must be between 01/01/1900 and 12/31/2099";

const toNumberOrNull = (value: string) =>
  value.trim() === "" ? null : Number(value);

const toAdvancedCriteria = ({
  dateType,
  dateFrom,
  dateTo,
  uwRetentionMin,
  uwRetentionMax,
  policiesMin,
  policiesMax,
}: AdvancedSearchCriteria): Record<string, string | number | null> => {
  const criteria: Record<string, string | number | null> = {};
  for (const { value } of PR_DATE_TYPE_OPTIONS) {
    const base = String(value);
    criteria[`${base}From`] = base === dateType ? dateFrom || null : null;
    criteria[`${base}To`] = base === dateType ? dateTo || null : null;
  }
  criteria.ActualRemittanceMin = toNumberOrNull(uwRetentionMin);
  criteria.ActualRemittanceMax = toNumberOrNull(uwRetentionMax);
  criteria.PolicyCountMin = toNumberOrNull(policiesMin);
  criteria.PolicyCountMax = toNumberOrNull(policiesMax);
  return criteria;
};

const TabContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: gaps.medium1,
});

const ToolbarRow = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  gap: gaps.small1,
  flexWrap: "wrap",
});

// uniqueIdentifier is derived, never a real column — it can't be hidden and it
// isn't offered in the chooser.
const PR_PROTECTED_FIELDS: (keyof PremiumReceivablesTransactionRecord)[] = [
  "uniqueIdentifier",
];

interface TransactionsTabProps {
  records: PremiumReceivablesTransactionRecord[];
  rowsPerPage?: number;
  onRowsPerPageChange?: (count: number) => void;
  page?: number;
  onPageChange?: (page: number) => void;
  totalRows?: number;
  totalPages?: number;
  isLoading?: boolean;
  sortColumn?: string;
  onSortChange?: (next: string) => void;
  onFilterChange?: (name: string, value: string | number | null) => void;
  onResetFilters?: () => void;
  applyEnabled?: boolean;
  onApplyFilters?: () => void;
  // False until the user has applied a filter, which distinguishes "nothing
  // searched yet" from "searched and found nothing" in the grid's empty state.
  hasSearched?: boolean;
  appliedCriteria?: Record<string, unknown>;
  userTimeZone?: string;
  // Advanced Search criteria, applied server-side alongside the header filters.
  onAdvancedSearchChange?: (
    criteria: Record<string, string | number | null>
  ) => void;
}

const TransactionsTab = ({
  records,
  rowsPerPage,
  onRowsPerPageChange,
  page,
  onPageChange,
  totalRows,
  totalPages,
  isLoading,
  sortColumn,
  onSortChange,
  onFilterChange,
  onResetFilters,
  applyEnabled,
  onApplyFilters,
  hasSearched,
  appliedCriteria,
  userTimeZone,
  onAdvancedSearchChange,
}: TransactionsTabProps) => {
  const [addPremiumReceivableOpen, setAddPremiumReceivableOpen] = useState(false);
  // Bumped on Reset so the table (and all its uncontrolled column-header
  // inputs) remount with fresh empty state.
  const [resetCounter, setResetCounter] = useState(0);

  const [advancedSearchOpen, setAdvancedSearchOpen] = useState(false);

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  // Local, not lifted/persisted: a Reset should always collapse expanded rows
  // rather than preserve them.
  const [expandedRowIds, setExpandedRowIds] = useState<string[]>([]);

  const [advancedValues, setAdvancedValues] = useState<Record<string, string>>(
    EMPTY_ADVANCED_VALUES
  );
  // Tracks whether Search has actually been applied to the grid, so Clear can
  // tell "typed but never searched" (nothing to reset on the grid) apart from
  // "a search is in effect" (grid needs to fall back to unfiltered).
  const [hasAppliedAdvanced, setHasAppliedAdvanced] = useState(false);

  const [lastEdited, setLastEdited] = useState<Record<string, RangeEnd>>({});
  const [dateReset, setDateReset] = useState(0);

  const dateTypeOption = PR_DATE_TYPE_OPTIONS.find(
    (o) => o.value === advancedValues.dateType
  );
  const isPeriodType = !!dateTypeOption?.period;

  const dateRangeDisabled = advancedValues.dateType === "";

  const handleAdvancedChange = useCallback(
    (key: string, value: string | OptionRenderType[]) => {
      const next = typeof value === "string" ? value : "";
      if (key === "dateType") {
        setDateReset((r) => r + 1);
        setLastEdited({});
        setAdvancedValues((prev) => ({
          ...prev,
          dateType: next,
          dateFrom: "",
          dateTo: "",
          periodFrom: "",
          periodTo: "",
        }));
        return;
      }
      const range = ADVANCED_RANGES.find(
        (r) => r.min === key || r.max === key
      );
      if (range) {
        setLastEdited((prev) => ({
          ...prev,
          [range.group]: range.min === key ? "min" : "max",
        }));
      }
      setAdvancedValues((prev) => ({ ...prev, [key]: next }));
    },
    []
  );

  // YYYYMM sorts lexicographically, so plain string comparison orders periods.
  const toYearMonthCode = (value: string) =>
    YEAR_MONTH_REGEX.test(value) ? toAccountingPeriod(value) : null;

  const periodFromCode = toYearMonthCode(advancedValues.periodFrom);
  const periodToCode = toYearMonthCode(advancedValues.periodTo);
  const periodFromIncomplete =
    advancedValues.periodFrom !== "" && periodFromCode === null;
  const periodToIncomplete =
    advancedValues.periodTo !== "" && periodToCode === null;

  const isOutOfPeriodRange = (code: string | null) =>
    code !== null && (code < "190001" || code > "209912");
  const periodInverted =
    !!periodFromCode && !!periodToCode && periodFromCode > periodToCode;
  const periodFromError =
    periodFromIncomplete ||
    isOutOfPeriodRange(periodFromCode) ||
    (periodInverted && lastEdited.period === "min");
  const periodToError =
    periodToIncomplete ||
    isOutOfPeriodRange(periodToCode) ||
    (periodInverted && lastEdited.period !== "min");
  const periodFromErrorText = periodFromIncomplete
    ? YEAR_MONTH_FORMAT_ERROR
    : isOutOfPeriodRange(periodFromCode)
    ? OUT_OF_RANGE_TEXT
    : "Cannot be after To Period";
  const periodToErrorText = periodToIncomplete
    ? YEAR_MONTH_FORMAT_ERROR
    : isOutOfPeriodRange(periodToCode)
    ? OUT_OF_RANGE_TEXT
    : "Cannot be before From Period";

  const isOutOfPickerRange = (value: string) =>
    value !== "" && (value < "1900-01-01" || value > "2099-12-31");
  const dateFromOutOfRange = isOutOfPickerRange(advancedValues.dateFrom);
  const dateToOutOfRange = isOutOfPickerRange(advancedValues.dateTo);
  const dateInverted =
    advancedValues.dateFrom !== "" &&
    advancedValues.dateTo !== "" &&
    advancedValues.dateFrom > advancedValues.dateTo;
  const dateFromError =
    dateFromOutOfRange || (dateInverted && lastEdited.date === "min");
  const dateToError =
    dateToOutOfRange || (dateInverted && lastEdited.date !== "min");

  const numericInverted = (min: string, max: string) =>
    min !== "" && max !== "" && Number(min) > Number(max);
  const uwInverted = numericInverted(
    advancedValues.uwRetentionMin,
    advancedValues.uwRetentionMax
  );
  const policiesInverted = numericInverted(
    advancedValues.policiesMin,
    advancedValues.policiesMax
  );

  const advancedSections: AdvancedSearchPanelSection[] = [
    {
      title: "Date",
      fields: [
        {
          key: "dateLabel",
          label: "Date",
          type: "label",
        },
        {
          key: "dateType",
          label: "Date Type",
          type: "select",
          options: PR_DATE_TYPE_OPTIONS,
        },
        ...(isPeriodType
          ? ([
              {
                key: "periodFrom",
                label: "",
                type: "text",
                placeholder: "YYYY/MM",
                inputComponent: YearMonthNumberFormat,
                error: periodFromError,
                errorText: periodFromErrorText,
              },
              {
                key: "periodRangeSeparator",
                label: "—",
                type: "separator",
              },
              {
                key: "periodTo",
                label: "",
                type: "text",
                placeholder: "YYYY/MM",
                inputComponent: YearMonthNumberFormat,
                error: periodToError,
                errorText: periodToErrorText,
              },
            ] as AdvancedSearchPanelField[])
          : ([
              {
                key: "dateFrom",
                label: "",
                type: "date",
                disabled: dateRangeDisabled,
                maxDate: parseIsoDate(advancedValues.dateTo),
                reset: dateReset,
                error: dateFromError,
                errorText: dateFromOutOfRange
                  ? OUT_OF_RANGE_TEXT
                  : "Cannot be after To Date",
              },
              {
                key: "dateRangeSeparator",
                label: "—",
                type: "separator",
              },
              {
                key: "dateTo",
                label: "",
                type: "date",
                disabled: dateRangeDisabled,
                minDate: parseIsoDate(advancedValues.dateFrom),
                reset: dateReset,
                error: dateToError,
                errorText: dateToOutOfRange
                  ? OUT_OF_RANGE_TEXT
                  : "Cannot be before From Date",
              },
            ] as AdvancedSearchPanelField[])),
      ],
    },
    {
      title: "UW Retention",
      fields: [
        {
          key: "uwRetentionLabel",
          label: "UW Retention",
          type: "label",
        },
        {
          key: "uwRetentionMin",
          label: "Minimum ($)",
          type: "number",
          error: uwInverted && lastEdited.uwRetention === "min",
          errorText: "Cannot be greater than Maximum",
        },
        {
          key: "uwRetentionMax",
          label: "Maximum ($)",
          type: "number",
          error: uwInverted && lastEdited.uwRetention !== "min",
          errorText: "Cannot be less than Minimum",
        },
      ],
    },
    {
      title: "Policies",
      fields: [
        {
          key: "policiesLabel",
          label: "Policies",
          type: "label",
        },
        {
          key: "policiesMin",
          label: "Minimum",
          type: "text",
          inputType: "number",
          inputProps: { min: 0, step: 1 },
          error: policiesInverted && lastEdited.policies === "min",
          errorText: "Cannot be greater than Maximum",
        },
        {
          key: "policiesMax",
          label: "Maximum",
          type: "text",
          inputType: "number",
          inputProps: { min: 0, step: 1 },
          error: policiesInverted && lastEdited.policies !== "min",
          errorText: "Cannot be less than Minimum",
        },
      ],
    },
  ];

  const hasAdvancedCriteria = Object.entries(advancedValues).some(
    ([key, value]) => key !== "dateType" && value !== ""
  );

  const hasAdvancedInput = Object.values(advancedValues).some(
    (value) => value !== ""
  );

  const applyAdvancedCriteria = useCallback(
    (criteria: AdvancedSearchCriteria) => {
      onAdvancedSearchChange?.(toAdvancedCriteria(criteria));
    },
    [onAdvancedSearchChange]
  );

  const handleAdvancedSearch = useCallback(() => {
    applyAdvancedCriteria({
      dateType: advancedValues.dateType,
      dateFrom: isPeriodType ? periodFromCode ?? "" : advancedValues.dateFrom,
      dateTo: isPeriodType ? periodToCode ?? "" : advancedValues.dateTo,
      uwRetentionMin: advancedValues.uwRetentionMin,
      uwRetentionMax: advancedValues.uwRetentionMax,
      policiesMin: advancedValues.policiesMin,
      policiesMax: advancedValues.policiesMax,
    });
    setHasAppliedAdvanced(true);
  }, [advancedValues, isPeriodType, periodFromCode, periodToCode, applyAdvancedCriteria]);

  const handleAdvancedClear = useCallback(() => {
    if (!hasAdvancedInput && !hasAppliedAdvanced) return;
    setAdvancedValues(EMPTY_ADVANCED_VALUES);
    setLastEdited({});
    setDateReset((r) => r + 1);
    if (hasAppliedAdvanced) {
      applyAdvancedCriteria(EMPTY_ADVANCED_CRITERIA);
      setHasAppliedAdvanced(false);
    }
  }, [hasAdvancedInput, hasAppliedAdvanced, applyAdvancedCriteria]);

  const handleFiltersChange = useCallback(
    (name: string, value: string | number | null) => {
      onFilterChange?.(name, value);
    },
    [onFilterChange]
  );

  const handleToggleRow = useCallback(
    (record: PremiumReceivablesTransactionRecord) => {
      setSelectedIds((prev) =>
        prev.includes(record.uniqueIdentifier)
          ? prev.filter((id) => id !== record.uniqueIdentifier)
          : [...prev, record.uniqueIdentifier]
      );
    },
    []
  );

  // Page-scoped, to match the header checkbox — StewartTableHeader computes
  // checked/indeterminate from the current page's rows only. Comparing total
  // selected against the page size instead (as Policy Management does) mis-fires
  // once ids accumulate across pages: 10 selected on page 1 then landing on a
  // 10-row page 2 makes the counts match, so select-all would clear.
  const handleToggleAllRows = useCallback(() => {
    const pageIds = records.map((r) => r.uniqueIdentifier);
    setSelectedIds((prev) => {
      const allOnPageSelected =
        pageIds.length > 0 && pageIds.every((id) => prev.includes(id));
      return allOnPageSelected
        ? prev.filter((id) => !pageIds.includes(id))
        : [...new Set([...prev, ...pageIds])];
    });
  }, [records]);

  const handleResetFilters = useCallback(() => {
    onResetFilters?.();
    setSelectedIds([]);
    setExpandedRowIds([]);
    setAdvancedValues(EMPTY_ADVANCED_VALUES);
    setLastEdited({});
    setDateReset((r) => r + 1);
    setResetCounter((c) => c + 1);
    setHasAppliedAdvanced(false);
  }, [onResetFilters]);

  const typeOptions = usePremiumReceivableTypeOptions();

  const defaultColumns = useMemo(
    () =>
      buildPrColumns({
        typeOptions,
        onFiltersChange: handleFiltersChange,
      }),
    [typeOptions, handleFiltersChange]
  );

  const {
    columns,
    hiddenColumns,
    onColumnResize,
    onColumnsModified,
    setColumnHidden,
    showAllColumns,
    hideAllColumns,
  } = useColumnSettings<PremiumReceivablesTransactionRecord>(
    PR_GRID_NAME,
    defaultColumns,
    PR_PROTECTED_FIELDS
  );

  const chooserOptions = useMemo<
    MenuColumnItem<PremiumReceivablesTransactionRecord>[]
  >(
    () =>
      defaultColumns
        .filter((c) => c.field !== "uniqueIdentifier")
        .map((c) => ({
          field: c.field,
          name: c.name,
          checked: !hiddenColumns.includes(c.field),
        })),
    [defaultColumns, hiddenColumns]
  );

  const handleColumnChecked = useCallback(
    (field: keyof PremiumReceivablesTransactionRecord, wasChecked: boolean) => {
      setColumnHidden(field, wasChecked);
    },
    [setColumnHidden]
  );

  // Export the visible columns, in display order, over the whole applied
  // search (ListCount 0 = every matching row, not just the current page).
  const exportedColumns = useMemo(
    () =>
      _orderBy(
        columns.filter((c) => !hiddenColumns.includes(c.field)),
        "position"
      ).map((c) => String(c.field)),
    [columns, hiddenColumns]
  );

  const handleExport = useCallback(async () => {
    const searchCriteria: Partial<PremiumReceivableCriteriaDto> = {
      // Same serialization the list call uses — a cleared header input leaves
      // an empty string behind, which would fail to bind to a DATE / INT param.
      ...serializeFilters(appliedCriteria ?? {}),
      UserTimeZone: userTimeZone,
      SortColumn: sortColumn,
      ListCount: 0,
      RequestedPage: 0,
    };
    await exportPremiumReceivable({
      ExportColumns: exportedColumns,
      SearchCriteria: searchCriteria,
    }, "/PremiumReceivable/Export", "Premium Receivables");
  }, [appliedCriteria, exportedColumns, sortColumn, userTimeZone]);

  return (
    <TabContainer>
      <ToolbarRow>
        <ActionButton
          variant="outlined"
          color="primary"
          onClick={() => setAddPremiumReceivableOpen(true)}
        >
          Add Premium Receivable
        </ActionButton>
        <ActionButton
          variant="outlined"
          color="secondary"
          onClick={handleResetFilters}
        >
          Reset
        </ActionButton>
        <AdvancedSearchButton
          open={advancedSearchOpen}
          onClick={() => setAdvancedSearchOpen((o) => !o)}
        />
        <ActionButton
          variant="outlined"
          color="primary"
          disabled={!applyEnabled}
          onClick={onApplyFilters}
        >
          Apply Filters
        </ActionButton>
        <Box sx={{ "& .MuiButton-root": { textTransform: "none" } }}>
          <ColumnsConfig
            options={chooserOptions}
            onChecked={handleColumnChecked}
            onShowAll={showAllColumns}
            onHideAll={hideAllColumns}
          />
        </Box>
        <ExportButton onClick={handleExport} />
      </ToolbarRow>

      <AdvancedSearchPanel
        key={`advanced-search-${resetCounter}`}
        open={advancedSearchOpen}
        sections={advancedSections}
        values={advancedValues}
        onChange={handleAdvancedChange}
        onClear={handleAdvancedClear}
        onSearch={handleAdvancedSearch}
        searchDisabled={!hasAdvancedCriteria}
      />

      <TransactionsTable
        key={`transactions-table-${resetCounter}`}
        records={records}
        columns={columns}
        hiddenColumns={hiddenColumns}
        onColumnResize={onColumnResize}
        onColumnsModified={onColumnsModified}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={onRowsPerPageChange}
        page={page}
        onPageChange={onPageChange}
        totalRows={totalRows}
        totalPages={totalPages}
        isLoading={isLoading}
        sortColumn={sortColumn}
        onSortChange={onSortChange}
        hasSearched={hasSearched}
        selectedIds={selectedIds}
        onToggleRow={handleToggleRow}
        onToggleAllRows={handleToggleAllRows}
        expandedRowIds={expandedRowIds}
        onExpandedRowIdsChange={setExpandedRowIds}
        userTimeZone={userTimeZone ?? ""}
      />

      {addPremiumReceivableOpen && (
        <AddPremiumReceivablesModal
          open={true}
          onClose={() => setAddPremiumReceivableOpen(false)}
        />
      )}
    </TabContainer>
  );
};

export default TransactionsTab;
